<script setup lang="ts">
import { useBreakpoints } from '@vueuse/core';
import * as LucideIcons from 'lucide-vue-next';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import ScrollFlashContainer from '@/components/ScrollFlashContainer.vue';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import MobileHelpButton from '@/components/ui/mobile-help-button.vue';
import MobileTooltipBadge from '@/components/ui/mobile-tooltip-badge.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import uiManifest from '@/config/ui-manifest-v2.json';

interface CallData {
  call: Record<string, any>;
}

interface UIManifestTab {
  key: string;
  label: string;
  icon?: string;
  badge?: boolean;
  badgeCondition?: string;
  sections: UIManifestSection[];
}

interface UIManifestSection {
  title: string;
  collapsible?: boolean;
  collapsed?: boolean;
  condition?: string;
  component?: string;
  variant?: string;
  grid?: number;
  fields: UIManifestField[];
}

interface UIManifestField {
  path: string;
  label: string;
  type: string;
  span?: number;
  icon?: string;
  color?: string;
  size?: string;
  highlight?: boolean;
  formatter?: string;
  numbered?: boolean;
  note?: string;
  placeholder?: string;
  priority?: string;
  severity?: string;
  condition?: string;
  hover?: string;
  evidence?: string;
}

const props = defineProps<{
  data: CallData;
  overrideTabs?: UIManifestTab[];
  hideTabBar?: boolean;
}>();

// Breakpoint detection for mobile
const breakpoints = useBreakpoints({ mobile: 0, tablet: 768 });
const isMobile = breakpoints.smaller('tablet');

// Use tabs from UI manifest file or override tabs if provided, filtered by data availability
const manifestTabs = computed(() => {
  const allTabs = props.overrideTabs || (uiManifest.views.detail.tabs as UIManifestTab[]);
  const filteredTabs = allTabs.filter(tab => hasTabData(tab, props.data.call));

  // Add transcript tab at the beginning on mobile only
  if (isMobile.value) {
    const transcriptTab: UIManifestTab = {
      key: 'transcript',
      label: 'Transcript',
      icon: 'headset',
      sections: [],
    };
    return [transcriptTab, ...filteredTabs];
  }

  return filteredTabs;
});

// Scrolling logic similar to DynamicLayout
const activeTabId = ref(manifestTabs.value[0]?.key || '');
const scrollContainerRef = ref<HTMLElement | null>(null);
const sectionRefs = ref<Record<string, HTMLElement>>({});
const observer = ref<IntersectionObserver | null>(null);
const isScrolling = ref(false);
const scrollTimer = ref<number | null>(null);

// Tab bar scrolling
const tabBarRef = ref<HTMLElement | null>(null);
const tabButtonRefs = ref<Record<string, HTMLElement>>({});
const shouldUseFlexLayout = ref(true); // 使用均匀分布布局

// 设置section元素的ref
function setSectionRef(tabId: string, el: HTMLElement | null) {
  if (el) {
    sectionRefs.value[tabId] = el;
  } else {
    delete sectionRefs.value[tabId];
  }
}

// 设置tab button的ref
function setTabButtonRef(tabId: string, el: HTMLElement | null) {
  if (el) {
    tabButtonRefs.value[tabId] = el;
  } else {
    delete tabButtonRefs.value[tabId];
  }
}

// 将活跃的tab滚动到可视区域中央（仅在滚动模式下使用）
function scrollTabToCenter(tabId: string) {
  // 如果是均匀分布模式，不需要滚动
  if (shouldUseFlexLayout.value) {return;}

  const tabButton = tabButtonRefs.value[tabId];
  const tabBar = tabBarRef.value;

  if (!tabButton || !tabBar) {return;}

  // 计算tab button相对于滚动容器的位置
  const tabLeft = tabButton.offsetLeft;
  const tabWidth = tabButton.offsetWidth;
  const barWidth = tabBar.clientWidth;

  // 计算目标滚动位置（让tab居中）
  const targetScrollLeft = tabLeft + tabWidth / 2 - barWidth / 2;

  // 平滑滚动到目标位置
  tabBar.scrollTo({
    left: Math.max(0, targetScrollLeft),
    behavior: 'smooth',
  });
}

// 平滑滚动到指定section
function scrollToSection(tabId: string) {
  const targetElement = sectionRefs.value[tabId];
  if (!targetElement || !scrollContainerRef.value) {return;}

  // 设置滚动状态
  isScrolling.value = true;
  if (scrollTimer.value) {
    clearTimeout(scrollTimer.value);
  }

  const tabsHeight = 60; // Tab栏大概高度

  if (isMobile.value) {
    // Try to find the main layout scroll container first
    const mainScrollContainer = document.querySelector('[data-scroll-container]');

    if (mainScrollContainer) {
      // Calculate position relative to the scroll container
      // target relative to viewport + current scroll - container relative to viewport - offset
      const targetTop =
        targetElement.getBoundingClientRect().top +
        mainScrollContainer.scrollTop -
        mainScrollContainer.getBoundingClientRect().top -
        tabsHeight -
        16;

      mainScrollContainer.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      });
    } else {
      // Fallback to window scroll if no container found
      const targetTop =
        targetElement.getBoundingClientRect().top + window.scrollY - tabsHeight - 16;
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      });
    }
  } else {
    // On desktop, scroll the container as before
    const scrollFlashInstance = scrollContainerRef.value as any;
    const actualScrollContainer = scrollFlashInstance?.containerRef;

    if (!actualScrollContainer || typeof actualScrollContainer.scrollTo !== 'function') {
      console.warn('无法找到可滚动的容器元素', {
        actualScrollContainer,
        scrollFlashInstance,
        hasContainerRef: !!scrollFlashInstance?.containerRef,
        containerRefType: typeof scrollFlashInstance?.containerRef,
      });
      isScrolling.value = false;
      return;
    }

    // 计算滚动位置 - 考虑Tab栏的高度
    const targetTop = targetElement.offsetTop - tabsHeight - 16; // 额外16px间距

    actualScrollContainer.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
  }

  // 设置滚动完成定时器 (800ms to match parent component timing)
  scrollTimer.value = setTimeout(() => {
    isScrolling.value = false;
    activeTabId.value = tabId;
  }, 800) as unknown as number;
}

// 手动点击Tab时的处理
function handleTabClick(tabId: string) {
  if (tabId === activeTabId.value) {return;}

  // Set isScrolling flag to disable observer updates during programmatic scroll
  isScrolling.value = true;
  if (scrollTimer.value !== null) {
    clearTimeout(scrollTimer.value);
  }

  // Special handling for transcript tab on mobile
  if (tabId === 'transcript' && isMobile.value) {
    const transcriptSection = document.getElementById('transcript-section');
    if (transcriptSection) {
      const tabsHeight = 60;
      const mainScrollContainer = document.querySelector('[data-scroll-container]');

      if (mainScrollContainer) {
        const targetTop =
          transcriptSection.getBoundingClientRect().top +
          mainScrollContainer.scrollTop -
          mainScrollContainer.getBoundingClientRect().top -
          tabsHeight -
          16;

        mainScrollContainer.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth',
        });
      } else {
        const targetTop =
          transcriptSection.getBoundingClientRect().top + window.scrollY - tabsHeight - 16;
        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth',
        });
      }
      activeTabId.value = tabId;

      // Reset isScrolling flag after scroll completes
      scrollTimer.value = setTimeout(() => {
        isScrolling.value = false;
      }, 800) as unknown as number;
    }
    return;
  }

  // 先滚动tab到中央
  scrollTabToCenter(tabId);

  // 然后滚动内容到对应section
  scrollToSection(tabId);
}

// 设置Intersection Observer来检测当前可见的section
function setupIntersectionObserver() {
  console.log('🚀 setupIntersectionObserver called:', {
    hasScrollContainerRef: !!scrollContainerRef.value,
    isMobile: isMobile.value,
    sectionsCount: Object.keys(sectionRefs.value).length,
  });

  if (!scrollContainerRef.value) {
    console.warn('❌ No scrollContainerRef, skipping observer setup');
    return;
  }

  // On mobile, use viewport as root (null). On desktop, use scroll container
  let observerRoot: Element | null = null;

  if (!isMobile.value) {
    // 获取ScrollFlashContainer内部的实际滚动容器 (desktop only)
    const scrollFlashInstance = scrollContainerRef.value as any;
    const actualScrollContainer = scrollFlashInstance?.containerRef;

    if (!actualScrollContainer) {
      console.warn('❌ 无法找到可滚动的容器元素用于Intersection Observer');
      return;
    }
    observerRoot = actualScrollContainer;
  }
  // On mobile, observerRoot stays null, which means use viewport
  console.log('✅ Observer will use:', observerRoot ? 'container' : 'viewport');

  observer.value = new IntersectionObserver(
    entries => {
      console.log('📊 Observer fired:', {
        isMobile: isMobile.value,
        isScrolling: isScrolling.value,
        entries: entries.length,
        root: observerRoot ? 'container' : 'viewport',
      });

      if (isScrolling.value) {return;} // 滚动过程中不更新activeTab

      // 找到当前最可见的section
      let maxVisibleRatio = 0;
      let mostVisibleTabId = '';

      entries.forEach(entry => {
        const tabId = entry.target.getAttribute('data-tab-id');
        console.log('  🔍', tabId, ':', entry.isIntersecting, entry.intersectionRatio.toFixed(2));
        if (!tabId) {return;}

        if (entry.isIntersecting && entry.intersectionRatio > maxVisibleRatio) {
          maxVisibleRatio = entry.intersectionRatio;
          mostVisibleTabId = tabId;
        }
      });

      console.log('  🎯 Winner:', mostVisibleTabId, maxVisibleRatio.toFixed(2));

      // 更新活跃Tab
      if (mostVisibleTabId && mostVisibleTabId !== activeTabId.value) {
        console.log('  ✅ Update activeTabId:', activeTabId.value, '→', mostVisibleTabId);
        activeTabId.value = mostVisibleTabId;
        // 当内容滚动导致tab变化时，也将对应tab滚动到中央
        nextTick(() => {
          scrollTabToCenter(mostVisibleTabId);
        });
      }
    },
    {
      root: observerRoot, // null on mobile (viewport), container on desktop
      rootMargin: '-20% 0px -60% 0px', // 上方20%，下方60%的区域内被认为是"可见"
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
    },
  );

  // 观察所有section
  const sectionsObserved = Object.keys(sectionRefs.value).length;
  console.log(
    '🔭 Setting up observer for',
    sectionsObserved,
    'sections:',
    Object.keys(sectionRefs.value),
  );
  Object.values(sectionRefs.value).forEach(sectionEl => {
    if (observer.value && sectionEl) {
      observer.value.observe(sectionEl);
    }
  });
}

// 清理资源
function cleanup() {
  if (observer.value) {
    observer.value.disconnect();
    observer.value = null;
  }

  if (scrollTimer.value) {
    clearTimeout(scrollTimer.value);
    scrollTimer.value = null;
  }
}

// 监听sectionRefs变化，重新设置observer
watch(
  () => sectionRefs.value,
  () => {
    nextTick(() => {
      cleanup();
      setupIntersectionObserver();
    });
  },
  { deep: true },
);

// 监听breakpoint变化，重新设置observer (mobile vs desktop有不同的root)
watch(isMobile, () => {
  nextTick(() => {
    cleanup();
    setupIntersectionObserver();
  });
});

onMounted(() => {
  nextTick(() => {
    setupIntersectionObserver();

    // Auto-scroll to Executive Summary element within ScrollFlashContainer
    setTimeout(() => {
      if (scrollContainerRef.value) {
        const scrollFlashInstance = scrollContainerRef.value as any;
        const actualScrollContainer = scrollFlashInstance?.containerRef;

        if (actualScrollContainer) {
          // Find Executive Summary element within this component's container
          const executiveSummaryElement =
            actualScrollContainer.querySelector('h3') &&
            [...actualScrollContainer.querySelectorAll('h3')].find((el: Element) =>
              el.textContent?.includes('Executive Summary'),
            );

          if (executiveSummaryElement && typeof actualScrollContainer.scrollTo === 'function') {
            // Calculate position relative to the scroll container with more offset to show content above
            const targetTop = (executiveSummaryElement as HTMLElement).offsetTop - 140;

            actualScrollContainer.scrollTo({
              top: Math.max(0, targetTop),
              behavior: 'smooth',
            });
          }
        }
      }
    }, 600);
  });
});

onUnmounted(() => {
  cleanup();
});

// Get value from nested path
function getNestedValue(obj: any, path: string | undefined = ''): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

// Format value based on type and formatter
function formatValue(value: any, type: string, formatter?: string): string {
  if (value === null || value === undefined || value === '') {return 'N/A';}

  // Handle objects - try to extract meaningful content
  if (typeof value === 'object' && value !== null) {
    // If it's an array, join the items
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'None';
    }

    // For objects, try common patterns
    if (value.name || value.label || value.title) {
      return String(value.name || value.label || value.title);
    }

    // If object has a value property
    if (value.value !== undefined) {
      return formatValue(value.value, type, formatter);
    }

    // If object has text content
    if (value.text || value.content) {
      return String(value.text || value.content);
    }

    // Handle key-value objects like {"sales": "intro_inquiry"}
    const keys = Object.keys(value);
    if (keys.length === 1) {
      const key = keys[0];
      const val = value[key];
      if (typeof val === 'string') {
        const formattedKey =
          formatter === 'humanize'
            ? key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : key;
        const formattedVal =
          formatter === 'humanize'
            ? val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : val;
        return `${formattedKey}: ${formattedVal}`;
      }
    }

    // For multiple key objects, create readable format
    if (keys.length > 1 && keys.length <= 3) {
      const pairs = keys
        .map(key => {
          const val = value[key];
          if (typeof val === 'string' || typeof val === 'number') {
            const formattedKey =
              formatter === 'humanize'
                ? key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                : key;
            const formattedVal =
              formatter === 'humanize' && typeof val === 'string'
                ? val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                : val;
            return `${formattedKey}: ${formattedVal}`;
          }
          return null;
        })
        .filter(Boolean);

      if (pairs.length > 0) {
        return pairs.join(', ');
      }
    }

    // Try to stringify nicely
    try {
      const str = JSON.stringify(value);
      if (str.length < 100) {
        return str;
      }
    } catch (e) {
      // Fallback if JSON.stringify fails
    }

    return 'Complex Data';
  }

  // Handle formatters first (more explicit than type)
  if (formatter === 'humanize') {
    return String(value)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  if (formatter === 'datetime') {
    return formatCallDateTime(value);
  }

  // Then handle type-specific formatting
  if (type === 'duration') {
    const seconds = Number(value);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  if (type === 'phone') {
    return formatPhoneNumber(String(value));
  }

  return String(value);
}

// Format phone number
function formatPhoneNumber(phone: string): string {
  // Handle undefined, null, or empty values
  if (!phone || phone === 'undefined' || phone === 'null') {
    return 'N/A';
  }

  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

// Get badge variant based on value
function getBadgeVariant(): 'default' | 'secondary' | 'destructive' | 'outline' {
  // value: string | boolean,
  // type: string,
  return 'secondary';
  // if (type === 'badge') {
  //   if (value === 'high' || value === 'inbound' || value === 'prospective_client') return 'default'
  //   if (value === 'medium' || value === 'existing_member') return 'secondary'
  //   if (value === 'low' || value === 'former_member') return 'outline'
  // }
  // if (type === 'boolean_badge') {
  //   return value === 'true' || value === true || value === 'Yes' ? 'default' : 'outline'
  // }
  // return 'secondary'
}

// Map UI manifest icon names to Lucide icon component names
const iconMap: Record<string, string> = {
  dashboard: 'LayoutDashboard',
  psychology: 'GraduationCap',
  person: 'User',
  assessment: 'TrendingUp',
  notification_important: 'AlertTriangle',
  headset: 'Headphones',
  code: 'Code',
  visibility: 'Eye',
  download: 'Download',
  school: 'GraduationCap',
  topic: 'MessageCircle',
  task_alt: 'CheckSquare',
  lightbulb: 'Lightbulb',
  format_quote: 'Quote',
  swap_horiz: 'Shuffle',
  thumb_up: 'ThumbsUp',
  emoji_objects: 'Lightbulb',
  report_problem: 'AlertTriangle',
  favorite: 'Heart',
  block: 'Ban',
  location_on: 'MapPin',
  warning: 'AlertTriangle',
  trending_up: 'TrendingUp',
  sentiment_satisfied: 'Smile',
  phone: 'Phone',
};

// Get icon component
function getIconComponent(iconName: string | undefined) {
  if (!iconName) {return null;}

  // First try the mapped name, then try the original name
  const mappedName = iconMap[iconName] || iconName;

  // Try to get the icon with common naming patterns
  const possibleNames = [
    mappedName,
    mappedName.charAt(0).toUpperCase() + mappedName.slice(1),
    iconName.charAt(0).toUpperCase() + iconName.slice(1),
  ];

  for (const name of possibleNames) {
    const icon = (LucideIcons as any)[name];
    if (icon) {return icon;}
  }

  return null;
}

// Check if a condition is met
function checkCondition(condition: string | undefined, data: any): boolean {
  if (!condition) {return true;}

  // Simple evaluation for basic conditions
  // For production, use a proper expression evaluator
  const parts = condition.split(/(\|\||&&)/);
  let result = true;

  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed === '||' || trimmed === '&&') {continue;}

    const value = getNestedValue(data, trimmed);
    if (trimmed.includes('!==')) {
      const [left, right] = trimmed.split('!==').map(s => s.trim());
      const leftValue = getNestedValue(data, left);
      result = leftValue !== right.replace(/['"]/g, '');
    } else {
      result = !!value;
    }
  }

  return result;
}

// Check if tab has any meaningful data to show
function hasTabData(tab: UIManifestTab, data: any): boolean {
  // Special handling for coaching tab
  // if (tab.key === 'coaching') {
  //   const coachingFields = [
  //     'coaching_next_time_try',
  //     'coaching_what_could_have_been_said',
  //     'coaching_alternative_approaches',
  //     'coaching_what_was_done_well',
  //     'scenario_identified',
  //   ]

  //   // Check if any coaching field has meaningful data
  //   const hasCoachingData = coachingFields.some((field) => {
  //     const value = getNestedValue(data, field)
  //     return value && value !== '' && value !== 'N/A'
  //       && !(Array.isArray(value) && value.length === 0)
  //   })

  //   return hasCoachingData
  // }

  // For other tabs, check if any field in any section has data
  for (const section of tab.sections) {
    if (!section?.fields) {
      console.error(section, section?.fields);
    }
    for (const field of section?.fields || []) {
      const value = getNestedValue(data, field.path);
      if (
        value &&
        value !== '' &&
        value !== 'N/A' &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        return true;
      }
    }
  }

  return false;
}

// Get sentiment emoji
function getSentimentEmoji(value: string): string {
  const sentimentMap: Record<string, string> = {
    very_satisfied: '😊',
    satisfied: '🙂',
    high: '😊',
    neutral: '😐',
    dissatisfied: '☹️',
    very_dissatisfied: '😠',
    low: '☹️',
  };
  return sentimentMap[value] || '😐';
}

// Get indicator icon and color
function getIndicator(value: string): { icon: string; color: string } {
  const indicatorMap: Record<string, { icon: string; color: string }> = {
    increase: { icon: 'TrendingUp', color: 'text-green-600' },
    decrease: { icon: 'TrendingDown', color: 'text-red-600' },
    unchanged: { icon: 'Minus', color: 'text-gray-500' },
  };
  return indicatorMap[value] || { icon: 'Minus', color: 'text-gray-500' };
}

// Expanded sections state
const expandedSections = ref<Set<string>>(new Set());

function toggleSection(sectionKey: string) {
  if (expandedSections.value.has(sectionKey)) {
    expandedSections.value.delete(sectionKey);
  } else {
    expandedSections.value.add(sectionKey);
  }
}

function isSectionExpanded(sectionKey: string, section: UIManifestSection): boolean {
  if (!section.collapsible) {return true;}
  if (section.collapsed && !expandedSections.value.has(sectionKey)) {return false;}
  return !section.collapsed || expandedSections.value.has(sectionKey);
}

// Emit events when activeTabId changes for parent to sync
const emit = defineEmits<{
  'update:activeTabId': [tabId: string];
}>();

watch(activeTabId, newId => {
  emit('update:activeTabId', newId);
});

// Expose data and methods for parent component
defineExpose({
  manifestTabs,
  activeTabId,
  handleTabClick,
  getIconComponent,
  checkCondition,
});
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Fixed Tab Bar with Horizontal Scrolling -->
    <div
      v-if="!hideTabBar"
      class="sticky top-0 z-50 md:z-10 bg-background/95 backdrop-blur-sm border-b"
    >
      <div class="py-2">
        <div class="relative">
          <!-- Dynamic Tab Container -->
          <div
            ref="tabBarRef"
            class="flex bg-muted p-1 rounded-lg scroll-smooth transition-all duration-200"
            :class="[shouldUseFlexLayout ? 'w-full' : 'overflow-x-auto scrollbar-hide']"
            style="scrollbar-width: none; -ms-overflow-style: none"
          >
            <TooltipProvider>
              <Tooltip v-for="tab in manifestTabs" :key="tab.key" :disable-hoverable-content="true">
                <TooltipTrigger as-child class="hidden md:inline-flex">
                  <button
                    :ref="el => setTabButtonRef(tab.key, el as HTMLElement)"
                    class="flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 overflow-hidden"
                    :class="[
                      shouldUseFlexLayout ? 'flex-1' : 'flex-shrink-0 min-w-fit whitespace-nowrap',
                      activeTabId === tab.key
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                    ]"
                    @click="handleTabClick(tab.key)"
                  >
                    <component
                      :is="getIconComponent(tab.icon)"
                      v-if="tab.icon && getIconComponent(tab.icon)"
                      class="h-4 w-4 flex-shrink-0"
                    />
                    <span class="truncate">{{ tab.label }}</span>
                    <Badge
                      v-if="tab.badge && checkCondition(tab.badgeCondition, data.call)"
                      variant="destructive"
                      class="ml-1 h-5 w-5 px-0 flex items-center justify-center rounded-full text-xs flex-shrink-0"
                    >
                      !
                    </Badge>
                  </button>
                </TooltipTrigger>
                <!-- Button without tooltip for mobile -->
                <button
                  :ref="el => setTabButtonRef(tab.key, el as HTMLElement)"
                  class="md:hidden flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 overflow-hidden"
                  :class="[
                    shouldUseFlexLayout ? 'flex-1' : 'flex-shrink-0 min-w-fit whitespace-nowrap',
                    activeTabId === tab.key
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                  ]"
                  @click="handleTabClick(tab.key)"
                >
                  <component
                    :is="getIconComponent(tab.icon)"
                    v-if="tab.icon && getIconComponent(tab.icon)"
                    class="h-4 w-4 flex-shrink-0"
                  />
                  <span class="truncate">{{ tab.label }}</span>
                  <Badge
                    v-if="tab.badge && checkCondition(tab.badgeCondition, data.call)"
                    variant="destructive"
                    class="ml-1 h-5 w-5 px-0 flex items-center justify-center rounded-full text-xs flex-shrink-0"
                  >
                    !
                  </Badge>
                </button>
                <TooltipContent class="hidden md:block">
                  <p>{{ tab.label }}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <!-- Scroll Fade Indicators (只在滚动模式下显示) -->
          <div
            v-if="!shouldUseFlexLayout"
            class="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-muted to-transparent pointer-events-none rounded-l-lg"
          />
          <div
            v-if="!shouldUseFlexLayout"
            class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-muted to-transparent pointer-events-none rounded-r-lg"
          />
        </div>
      </div>
    </div>

    <!-- Scrollable Content Container with ScrollFlashContainer -->
    <div class="flex-1 min-h-0 relative">
      <ScrollFlashContainer
        ref="scrollContainerRef"
        :flash-count="3"
        :flash-interval="150"
        :flash-on-init="true"
        class="h-[calc(100vh-260px)] overflow-y-auto"
      >
        <div>
          <!-- All Tab Contents Combined -->
          <template v-for="(tab, index) in manifestTabs" :key="tab.key">
            <!-- Skip rendering transcript section on mobile - it's in parent component -->
            <div
              v-if="!(isMobile && tab.key === 'transcript')"
              :ref="el => setSectionRef(tab.key, el as HTMLElement)"
              :data-tab-id="tab.key"
              class="scroll-mt-20"
              :class="[{ 'mt-8': index > 0 }]"
            >
              <!-- Tab Section Title -->
              <div class="mb-6">
                <div class="flex items-center gap-3 mb-4 mt-4 ml-[24px]">
                  <h2 class="text-2xl font-semibold text-foreground">
                    {{ tab.label }}
                  </h2>
                </div>
                <div class="h-px bg-gradient-to-r from-primary/20 via-primary/10 to-transparent" />
              </div>

              <!-- Tab Sections -->
              <div class="space-y-6">
                <div
                  v-for="(section, sectionIndex) in tab.sections"
                  v-show="checkCondition(section.condition, data.call)"
                  :key="`${tab.key}-section-${sectionIndex}`"
                >
                  <!-- Special component rendering -->
                  <Card
                    v-if="section.component === 'CoachingCard'"
                    class="border-primary/20 bg-primary/5"
                  >
                    <CardHeader>
                      <CardTitle class="flex items-center justify-between">
                        {{ section.title }}
                        <Badge variant="default"> Coaching </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent class="space-y-4">
                      <div v-for="field in section.fields" :key="field.path">
                        <div v-if="field.type === 'header'" class="text-lg font-semibold">
                          {{
                            formatValue(
                              getNestedValue(data.call, field.path),
                              field.type,
                              field.formatter,
                            )
                          }}
                        </div>
                        <div
                          v-else-if="field.type === 'highlight'"
                          class="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md"
                        >
                          <component
                            :is="getIconComponent(field.icon)"
                            v-if="field.icon && getIconComponent(field.icon)"
                            class="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0"
                          />
                          <div>
                            <div class="font-medium text-sm text-muted-foreground">
                              {{ field.label }}
                            </div>
                            <div
                              class="mt-1"
                              :class="[
                                (getNestedValue(data.call, field.path) || 'N/A') === 'N/A'
                                  ? 'text-muted-foreground/60'
                                  : '',
                              ]"
                            >
                              {{ getNestedValue(data.call, field.path) || 'N/A' }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <!-- Process Checklist -->
                  <Card v-else-if="section.component === 'ProcessChecklist'">
                    <CardHeader
                      class="cursor-pointer"
                      @click="toggleSection(`${tab.key}-${sectionIndex}`)"
                    >
                      <CardTitle class="flex items-center justify-between">
                        {{ section.title }}
                        <component
                          :is="
                            getIconComponent(
                              isSectionExpanded(`${tab.key}-${sectionIndex}`, section)
                                ? 'ChevronUp'
                                : 'ChevronDown',
                            )
                          "
                          class="h-4 w-4"
                        />
                      </CardTitle>
                    </CardHeader>
                    <CardContent
                      v-show="isSectionExpanded(`${tab.key}-${sectionIndex}`, section)"
                      class="space-y-3"
                    >
                      <div v-for="field in section.fields" :key="field.path" class="space-y-2">
                        <div v-if="field.type === 'checkbox'" class="flex items-start gap-3">
                          <Checkbox
                            :checked="getNestedValue(data.call, field.path)"
                            disabled
                            class="mt-1"
                          />
                          <div class="flex-1">
                            <div class="font-medium">
                              {{ field.label }}
                            </div>
                            <div
                              v-if="field.note && getNestedValue(data.call, field.note)"
                              class="text-sm text-muted-foreground mt-1"
                            >
                              <div class="flex items-center gap-1">
                                <LucideIcons.Lightbulb :size="14" class="text-yellow-500" />
                                <span>{{ getNestedValue(data.call, field.note) }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else-if="field.highlight" class="p-3 bg-muted rounded-md">
                          <div class="font-medium text-sm text-muted-foreground mb-1">
                            {{ field.label }}
                          </div>
                          <div
                            :class="[
                              (getNestedValue(data.call, field.path) || 'N/A') === 'N/A'
                                ? 'text-muted-foreground/60'
                                : '',
                            ]"
                          >
                            {{ getNestedValue(data.call, field.path) || 'N/A' }}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <!-- Alert List -->
                  <div v-else-if="section.component === 'AlertList'" class="space-y-3">
                    <h3 class="text-lg font-semibold">
                      {{ section.title }}
                    </h3>
                    <div v-for="field in section.fields" :key="field.path">
                      <Alert
                        v-if="field.type === 'alert' && getNestedValue(data.call, field.path)"
                        :variant="field.severity === 'high' ? 'destructive' : 'default'"
                      >
                        <component :is="getIconComponent('AlertTriangle')" class="h-4 w-4" />
                        <AlertTitle>{{ field.label }}</AlertTitle>
                        <AlertDescription
                          v-if="
                            field.path === 'f_call_reasons' && getNestedValue(data.call, field.path)
                          "
                        >
                          <ul class="list-disc list-inside mt-2">
                            <li
                              v-for="(reason, idx) in getNestedValue(data.call, field.path)"
                              :key="idx"
                            >
                              {{ reason }}
                            </li>
                          </ul>
                        </AlertDescription>
                      </Alert>
                    </div>
                  </div>

                  <!-- Metrics Grid -->
                  <Card v-else-if="section.component === 'MetricsGrid'">
                    <CardHeader>
                      <CardTitle>{{ section.title }}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div class="grid grid-cols-3 gap-4">
                        <div v-for="field in section.fields" :key="field.path" class="text-center">
                          <div class="text-sm text-muted-foreground mb-1">
                            {{ field.label }}
                          </div>
                          <div class="text-2xl font-bold">
                            <span
                              v-if="getNestedValue(data.call, field.path)"
                              class="text-green-600"
                              >✓</span
                            >
                            <span v-else class="text-gray-400">✗</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <!-- Text Panel -->
                  <Card v-else-if="section.component === 'TextPanel'" class="bg-muted/30">
                    <CardHeader v-if="section.title">
                      <CardTitle>{{ section.title }}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div v-for="field in section.fields" :key="field.path">
                        <div
                          v-if="field.highlight"
                          class="p-4 bg-background rounded-md font-medium"
                          :class="[
                            (getNestedValue(data.call, field.path) || 'No summary available') ===
                            'No summary available'
                              ? 'text-muted-foreground/60'
                              : '',
                          ]"
                        >
                          {{ getNestedValue(data.call, field.path) || 'No summary available' }}
                        </div>
                        <div
                          v-else
                          :class="[
                            (getNestedValue(data.call, field.path) || 'N/A') === 'N/A'
                              ? 'text-muted-foreground/60'
                              : '',
                          ]"
                        >
                          {{ getNestedValue(data.call, field.path) || 'N/A' }}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <!-- Default Card Section -->
                  <Card v-else>
                    <CardHeader v-if="section.title">
                      <CardTitle>{{ section.title }}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div
                        :class="[
                          section.grid ? `grid gap-4` : 'space-y-4',
                          section.grid === 2 && 'grid-cols-1 md:grid-cols-2',
                          section.grid === 3 && 'grid-cols-1 md:grid-cols-3',
                          section.grid === 4 && 'grid-cols-2 md:grid-cols-4',
                        ]"
                      >
                        <div
                          v-for="field in section.fields"
                          :key="field.path"
                          :class="[
                            field.span === 2 && 'md:col-span-2',
                            field.span === 3 && 'md:col-span-3',
                            field.span === 4 && 'md:col-span-4',
                          ]"
                        >
                          <!-- Different field type renderings -->
                          <div v-if="field.type === 'badge'" class="space-y-1">
                            <div class="text-sm text-muted-foreground">
                              {{ field.label }}
                            </div>

                            <MobileTooltipBadge
                              :variant="getBadgeVariant()"
                              :size="field.size === 'large' ? 'large' : 'default'"
                              :content="
                                formatValue(
                                  getNestedValue(data.call, field.path),
                                  field.type,
                                  field.formatter,
                                )
                              "
                              :tooltip-text="
                                (field.hover || field.evidence) &&
                                getNestedValue(data.call, field.hover || field.evidence)
                                  ? getNestedValue(data.call, field.hover || field.evidence)
                                  : undefined
                              "
                            />
                          </div>

                          <div v-else-if="field.type === 'boolean_badge'" class="space-y-1">
                            <div class="text-sm text-muted-foreground">
                              {{ field.label }}
                            </div>
                            <Badge
                              :variant="
                                getNestedValue(data.call, field.path) ? 'default' : 'outline'
                              "
                            >
                              {{ getNestedValue(data.call, field.path) ? 'Yes' : 'No' }}
                            </Badge>
                          </div>

                          <div v-else-if="field.type === 'sentiment'" class="space-y-1">
                            <div class="text-sm text-muted-foreground">
                              {{ field.label }}
                            </div>
                            <div class="flex items-center gap-3">
                              <div class="text-2xl">
                                {{ getSentimentEmoji(getNestedValue(data.call, field.path)) }}
                              </div>
                              <div class="text-sm">
                                {{
                                  formatValue(
                                    getNestedValue(data.call, field.path),
                                    field.type,
                                    field.formatter,
                                  )
                                }}
                              </div>
                            </div>
                          </div>

                          <div v-else-if="field.type === 'indicator'" class="space-y-1">
                            <div class="text-sm text-muted-foreground">
                              {{ field.label }}
                            </div>
                            <div class="flex items-center gap-2">
                              <component
                                :is="
                                  getIconComponent(
                                    getIndicator(getNestedValue(data.call, field.path)).icon,
                                  )
                                "
                                class="h-5 w-5"
                                :class="[
                                  getIndicator(getNestedValue(data.call, field.path)).color,
                                  field.size === 'large' && 'h-8 w-8',
                                ]"
                              />
                              <span :class="field.size === 'large' && 'text-lg font-medium'">
                                {{
                                  formatValue(
                                    getNestedValue(data.call, field.path),
                                    field.type,
                                    field.formatter,
                                  )
                                }}
                              </span>
                            </div>
                          </div>

                          <div v-else-if="field.type === 'list'" class="space-y-2">
                            <div class="flex items-center gap-2">
                              <component
                                :is="getIconComponent(field.icon)"
                                v-if="field.icon && getIconComponent(field.icon)"
                                class="h-4 w-4"
                                :class="[
                                  field.color === 'green' && 'text-green-600',
                                  field.color === 'orange' && 'text-orange-600',
                                  field.color === 'blue' && 'text-blue-600',
                                  field.color === 'red' && 'text-red-600',
                                ]"
                              />
                              <div class="text-sm font-medium text-muted-foreground">
                                {{ field.label }}
                              </div>
                            </div>
                            <ul
                              v-if="
                                getNestedValue(data.call, field.path) &&
                                Array.isArray(getNestedValue(data.call, field.path))
                              "
                              class="list-inside space-y-1 text-sm"
                              :class="[field.numbered ? 'list-decimal' : 'list-disc']"
                            >
                              <li
                                v-for="(item, idx) in getNestedValue(data.call, field.path)"
                                :key="idx"
                              >
                                {{ item }}
                              </li>
                            </ul>
                            <div v-else class="text-sm text-muted-foreground">None</div>
                          </div>

                          <div v-else-if="field.type === 'checklist'" class="space-y-2">
                            <div class="flex items-center gap-2">
                              <component
                                :is="getIconComponent(field.icon || 'CheckSquare')"
                                v-if="getIconComponent(field.icon || 'CheckSquare')"
                                class="h-4 w-4"
                                :class="[field.priority === 'high' && 'text-red-600']"
                              />
                              <div class="text-sm font-medium text-muted-foreground">
                                {{ field.label }}
                              </div>
                            </div>
                            <div
                              v-if="
                                getNestedValue(data.call, field.path) &&
                                Array.isArray(getNestedValue(data.call, field.path))
                              "
                              class="space-y-2"
                            >
                              <div
                                v-for="(item, idx) in getNestedValue(data.call, field.path)"
                                :key="idx"
                                class="flex items-start gap-2"
                              >
                                <Checkbox checked disabled class="mt-0.5" />
                                <span class="text-sm">{{ item }}</span>
                              </div>
                            </div>
                            <div v-else class="text-sm text-muted-foreground">
                              No actions required
                            </div>
                          </div>

                          <div v-else-if="field.type === 'tags'" class="space-y-2">
                            <div class="text-sm text-muted-foreground">
                              {{ field.label }}
                            </div>
                            <div class="flex flex-wrap gap-2">
                              <Badge
                                v-for="(tag, idx) in getNestedValue(data.call, field.path) || []"
                                :key="idx"
                                variant="secondary"
                              >
                                {{ tag }}
                              </Badge>
                            </div>
                          </div>

                          <div
                            v-else-if="field.type === 'quote'"
                            class="border-l-4 border-primary pl-4 py-2"
                          >
                            <div class="flex items-start gap-2">
                              <component
                                :is="getIconComponent(field.icon || 'Quote')"
                                v-if="getIconComponent(field.icon || 'Quote')"
                                class="h-4 w-4 text-muted-foreground mt-0.5"
                              />
                              <div>
                                <div class="text-sm text-muted-foreground mb-1">
                                  {{ field.label }}
                                </div>
                                <div
                                  class="italic"
                                  :class="[
                                    (getNestedValue(data.call, field.path) || 'N/A') === 'N/A'
                                      ? 'text-muted-foreground/60'
                                      : '',
                                  ]"
                                >
                                  "{{ getNestedValue(data.call, field.path) || 'N/A' }}"
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Default text field -->
                          <div v-else class="space-y-1">
                            <div class="text-sm text-muted-foreground">
                              {{ field.label }}
                            </div>
                            <div class="flex items-center gap-2">
                              <div
                                :class="[
                                  field.highlight && 'font-medium',
                                  formatValue(
                                    getNestedValue(data.call, field.path),
                                    field.type,
                                    field.formatter,
                                  ) === 'N/A'
                                    ? 'text-muted-foreground/60'
                                    : '',
                                ]"
                              >
                                {{
                                  formatValue(
                                    getNestedValue(data.call, field.path),
                                    field.type,
                                    field.formatter,
                                  )
                                }}
                              </div>
                              <MobileHelpButton
                                v-if="
                                  (field.hover || field.evidence) &&
                                  getNestedValue(data.call, field.hover || field.evidence || '')
                                "
                                :text="
                                  getNestedValue(data.call, field.hover || field.evidence || '')
                                "
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </template>
        </div>
      </ScrollFlashContainer>
    </div>
  </div>
</template>

<style scoped>
/* Hide scrollbar for webkit browsers */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for Firefox */
.scrollbar-hide {
  scrollbar-width: none;
}
</style>
